import React, { useState } from "react";
import { FaDownload } from "react-icons/fa";
import AxiosInstance, { TokenService } from "../../../services/Api";
import { CustomButton } from "../../../components";
import styles from "./StudentAttendanceReport.module.css";

function StudentAttendanceReport() {
  const [allAttendanceLoading, setAllAttendanceLoading] = useState(false);
  const [absentLeaveLoading, setAbsentLeaveLoading] = useState(false);
  const [modalState, setModalState] = useState({
    open: false,
    type: "",
    message: "",
    redirectPath: "",
  });

  // Get school_id from storage / context (adjust to your app)
  // const schoolId = Number(localStorage.getItem("school_id")) || 7;
    const user = TokenService.getUser();
    const clientData = user?.client_data[0] || {};
    const schoolId = clientData?.school_id || "";

  // Today's date in YYYY-MM-DD format
  const getToday = () => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  // Format ISO date to readable YYYY-MM-DD
  const formatDate = (isoDate) => {
    if (!isoDate) return "";
    return isoDate.split("T")[0];
  };

  // Escape CSV cell values
  const escapeCSVValue = (value) => {
    if (value === null || value === undefined) return "";
    const str = String(value);
    if (str.includes(",") || str.includes('"') || str.includes("\n")) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  // Convert array of objects to CSV string
  const convertToCSV = (data, headers) => {
    const headerRow = headers.map((h) => escapeCSVValue(h.label)).join(",");
    const rows = data.map((row) =>
      headers.map((h) => escapeCSVValue(row[h.key])).join(",")
    );
    return [headerRow, ...rows].join("\n");
  };

  // Trigger file download
  const downloadCSV = (csvContent, fileName) => {
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Common headers for both exports
  const attendanceHeaders = [
    { key: "emis_id", label: "EMIS ID" },
    { key: "student_name", label: "Student Name" },
    { key: "class_name", label: "Class Name" },
    { key: "section_id", label: "Section Name" },
    { key: "attendance_date", label: "Attendance Date" },
    { key: "status", label: "Status" },
  ];

  // Export All Attendance Today
  const handleExportAllAttendance = async () => {
    try {
      setAllAttendanceLoading(true);
      const attendanceDate = getToday();

      const response = await AxiosInstance.post(
        "/attendance/export/attendanceList",
        {
          school_id: schoolId,
          attendance_date: attendanceDate,
        }
      );

      if (response.data?.status && response.data.data) {
        const { students } = response.data.data;

        if (!students || students.length === 0) {
          setModalState({
            open: true,
            type: "success",
            message: "No attendance data available to download.",
            redirectPath: "",
          });
          return;
        }

        const formattedData = students.map((s) => ({
          emis_id: s.emis_id,
          student_name: s.student_name || s.name,
          class_name: s.class_name,
          section_id: s.section_id,
          attendance_date: formatDate(s.attendance_date),
          status: s.attendance,
        }));

        const csvContent = convertToCSV(formattedData, attendanceHeaders);
        downloadCSV(csvContent, `ALL_ATTENDANCE_${attendanceDate}.csv`);
      } else {
        setModalState({
          open: true,
          type: "error",
          message:
            response.data?.message || "Failed to fetch attendance data.",
          redirectPath: "",
        });
      }
    } catch (error) {
      console.error("Error downloading all attendance:", error);
      setModalState({
        open: true,
        type: "error",
        message: "Failed to download attendance list. Please try again.",
        redirectPath: "",
      });
    } finally {
      setAllAttendanceLoading(false);
    }
  };

  // Export Absent & Leave Today
  const handleExportAbsentLeave = async () => {
    try {
      setAbsentLeaveLoading(true);
      const attendanceDate = getToday();

      const response = await AxiosInstance.post(
        "/attendance/export/absentLeaveList",
        {
          school_id: schoolId,
          attendance_date: attendanceDate,
        }
      );

      if (response.data?.status && response.data.data) {
        const { students } = response.data.data;

        if (!students || students.length === 0) {
          setModalState({
            open: true,
            type: "success",
            message: "No absent or leave data available to download.",
            redirectPath: "",
          });
          return;
        }

        const formattedData = students.map((s) => ({
          emis_id: s.emis_id,
          student_name: s.student_name,
          class_name: s.class_name,
          section_id: s.section_id,
          attendance_date: formatDate(s.attendance_date),
          status: s.type,
        }));

        const csvContent = convertToCSV(formattedData, attendanceHeaders);
        downloadCSV(csvContent, `ABSENT_LEAVE_${attendanceDate}.csv`);
      } else {
        setModalState({
          open: true,
          type: "error",
          message:
            response.data?.message || "Failed to fetch absent/leave data.",
          redirectPath: "",
        });
      }
    } catch (error) {
      console.error("Error downloading absent/leave list:", error);
      setModalState({
        open: true,
        type: "error",
        message: "Failed to download absent/leave list. Please try again.",
        redirectPath: "",
      });
    } finally {
      setAbsentLeaveLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.buttonGroup}>
          <CustomButton
            type="button"
            variant="contained"
            className={`${styles.downloadButton}`}
            fullWidth={false}
            onClick={handleExportAllAttendance}
            disabled={allAttendanceLoading}
          >
            <FaDownload style={{ marginRight: "8px" }} />
            {allAttendanceLoading
              ? "Downloading..."
              : "EXPORT ALL ATTENDANCE TODAY"}
          </CustomButton>

          <CustomButton
            type="button"
            variant="contained"
            className={`${styles.downloadButton} ms-2`}
            fullWidth={false}
            onClick={handleExportAbsentLeave}
            disabled={absentLeaveLoading}
          >
            <FaDownload style={{ marginRight: "8px" }} />
            {absentLeaveLoading
              ? "Downloading..."
              : "EXPORT ABSENT & LEAVE TODAY"}
          </CustomButton>
        </div>
      </div>

      {modalState.open && (
        <div style={{ display: "none" }}>{modalState.message}</div>
      )}
    </div>
  );
}

export default StudentAttendanceReport;